import { appConfig } from "@/config";
import { authApi } from "@/core/auth/services";
import { cameraApi } from "@/core/cameraApi";
import { captureApi } from "@/modules/capture/services/captureApi";
import { dashboardConfigApi } from "@/modules/dashboard/services";
import { modelApi } from "@/modules/model/services";
import { projectApi } from "@/modules/project/services/projectApi";
import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { Input } from "@/modules/shadcn/ui/input";
import { Label } from "@/modules/shadcn/ui/label";
import { useAppDispatch } from "@/hooks/redux";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { organizationApi, useDeleteOrganizationMutation } from "../../services/organizationApi";

interface DangerZoneProps {
  organization: { id: number; name: string };
}

export const DangerZone = ({ organization }: DangerZoneProps) => {
  const { t } = useTranslation(["account", "common"]);
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleteOrganization, { isLoading }] = useDeleteOrganizationMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isConfirmed = confirmText.trim() === organization.name;

  const handleConfirm = async () => {
    try {
      await deleteOrganization().unwrap();
      dispatch(projectApi.util.resetApiState());
      dispatch(organizationApi.util.resetApiState());
      dispatch(modelApi.util.resetApiState());
      dispatch(captureApi.util.resetApiState());
      dispatch(cameraApi.util.resetApiState());
      dispatch(dashboardConfigApi.util.resetApiState());
      dispatch(authApi.util.resetApiState());
      navigate(appConfig.web.routes.auth.selectOrganization, { replace: true });
      toast.success(t("dangerZone.deleted"));
    } catch {
      toast.error(t("dangerZone.deleteFailed"));
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <h5 className="text-xl font-semibold text-destructive">{t("dangerZone.title")}</h5>
        <p className="text-sm text-muted-foreground">
          {t("dangerZone.body")}
        </p>
        <div>
          <Button variant="destructive" onClick={() => setOpen(true)}>
            {t("dangerZone.delete")}
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => !isLoading && setOpen(o)}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className="text-base font-bold">{t("dangerZone.confirmTitle")}</DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="dangerZone.confirmBody"
                values={{ name: organization.name }}
                components={{ strong: <strong /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="confirmOrgName">{t("dangerZone.nameLabel")}</Label>
            <Input
              id="confirmOrgName"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={organization.name}
              disabled={isLoading}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setOpen(false); setConfirmText(""); }}
              disabled={isLoading}
            >
              {t("common:actions.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleConfirm}
              disabled={!isConfirmed || isLoading}
            >
              {isLoading ? t("dangerZone.deleting") : t("dangerZone.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
